import { useState } from "react";
import { Minus, Plus, Trash2, AlertTriangle, ShoppingBag } from "lucide-react";
import { IconButton, Button } from "@printtat/ui";
import { formatPrice } from "@printtat/shared";
import type { CartItem } from "@printtat/shared";
import { getDefaultMockupPath } from "@/utils/productAssets";

interface CartItemRowProps {
  item: CartItem;
  onUpdateQuantity?: (id: string, quantity: number) => void;
  onRemove?: (id: string) => void;
  compact?: boolean;
}

export const CartItemRow = ({
  item,
  onUpdateQuantity,
  onRemove,
  compact = false,
}: CartItemRowProps) => {
  const [imgError, setImgError] = useState(false);
  const lineTotal = item.unitPrice * item.quantity;

  const previewSrc = imgError
    ? null
    : item.previewUrl ??
      (item.productSlug ? getDefaultMockupPath(item.productSlug, "front") : null);

  const hasStockIssue =
    item.stockAvailable === 0 ||
    (item.stockAvailable > 0 && item.stockAvailable < item.quantity);
  const stockLabel =
    item.stockAvailable === 0
      ? "Out of stock"
      : `Only ${item.stockAvailable} in stock`;

  const dimmed = hasStockIssue ? "opacity-50" : "";

  return (
    <div className="py-3">
      <div className="flex gap-3">
        {/* Preview */}
        <div
          className={`flex-shrink-0 w-16 h-16 rounded-lg bg-gray-100 overflow-hidden ${dimmed}`}
        >
          {previewSrc ? (
            <img
              src={previewSrc}
              alt={item.designName}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-gray-300" />
            </div>
          )}
        </div>

        {/* Info + controls */}
        <div className="flex-1 min-w-0">
          <div className={`flex items-start justify-between gap-2 ${dimmed}`}>
            <div className="min-w-0">
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {item.designName}
              </h4>
              <p className="text-xs text-gray-400 mt-0.5">
                {item.productName} &middot; {item.variantLabel}
              </p>
            </div>

            {/* Price — far right */}
            <div className="flex-shrink-0 text-right">
              <p className="text-sm font-semibold text-gray-900">
                {formatPrice(lineTotal)}
              </p>
              {item.quantity > 1 && (
                <p className="text-[10px] text-gray-400">
                  {formatPrice(item.unitPrice)} / unit
                </p>
              )}
            </div>
          </div>

          {/* Stock issue — full opacity */}
          {hasStockIssue && (
            <div className="flex items-center mt-2">
              <p className="text-xs text-red-600 font-medium">{stockLabel}</p>
              {onRemove && (
                <Button
                  variant="ghost"
                  color="red"
                  size="sm"
                  onClick={() => onRemove(item.id)}
                  className="ml-auto"
                >
                  <Trash2 size={12} />
                  Remove
                </Button>
              )}
            </div>
          )}

          {/* Normal controls — only when no stock issue */}
          {!compact && onUpdateQuantity && onRemove && !hasStockIssue && (
            <div className="flex items-center gap-2 mt-2">
              <IconButton
                size="xs"
                variant="outline"
                onClick={() =>
                  onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))
                }
                disabled={item.quantity <= 1}
              >
                <Minus size={12} />
              </IconButton>
              <span className="text-xs font-medium text-gray-700 w-6 text-center">
                {item.quantity}
              </span>
              <IconButton
                size="xs"
                variant="outline"
                onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
              >
                <Plus size={12} />
              </IconButton>
              <IconButton
                size="xs"
                className="ml-auto text-red-400 hover:text-red-600 hover:bg-red-50"
                onClick={() => onRemove(item.id)}
              >
                <Trash2 size={12} />
              </IconButton>
            </div>
          )}

          {compact && !hasStockIssue && (
            <p className="text-xs text-gray-500 mt-1">Qty: {item.quantity}</p>
          )}
        </div>
      </div>

      {/* Price changed warning banner */}
      {item.isStale && (
        <div className="flex items-center gap-1.5 mt-2 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-md">
          <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
          <p className="text-[11px] text-amber-700 font-medium">
            Price changed from{" "}
            {item.stalePriceFrom != null
              ? formatPrice(item.stalePriceFrom)
              : "?"}{" "}
            to {formatPrice(item.unitPrice)}
          </p>
        </div>
      )}
    </div>
  );
};
